import { useContext, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import styled from 'styled-components';
import { CircularProgress } from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import FlagIcon from '@mui/icons-material/Flag';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import PeopleIcon from '@mui/icons-material/People';
import EditNoteIcon from '@mui/icons-material/EditNote';
import LanguageIcon from '@mui/icons-material/Language';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { useCrmTenant } from '../crm/useCrmTenant';
import { FormRuntimeContext } from '../form-renderer/FormRuntimeContext';
import {
  AiConfigKey,
  AiGeneratorAiConfig,
  AiGeneratorBlockConfig,
  AiGeneratorOutputFormat,
  AiGeneratorPrompt,
} from '../../types';
import { composeOutput } from './aiGenerator/composeOutput';
import {
  AI_CONFIG_KEYS,
  AI_OVERRIDE_PREFIX,
  AI_TYPES,
  LANGUAGES,
  MODELS,
  TONES,
  getApplicableGoals,
  getSourceForType,
  resolveAiConfig,
  resolveRespondentEditable,
} from './aiGenerator/aiConfig';
import { ChipOption, SettingChip } from './aiGenerator/SettingChip';
import {
  AiBlockInput,
  buildBlocksUserInput,
  extractAiText,
  parseBlockOutputs,
  resolveBlockPrompt,
  toBlockResults,
} from './aiGenerator/aiBlocks';
import { FabaiPersona, fetchPersonas, generateFabai, personaDisplayName, personaId, personaName, resolvePersonaId } from '../../services/api/fabaiApi';

/** The label lengths the AI service is willing to accept, as the backend enforced. */
const MAX_LABEL = 200;
const MAX_PROMPT = 1000;

/** Turns a failed request into something the visitor can read. */
const errorMessage = (error: any): string => {
  if (error.response?.status === 429) {
    return 'Too many requests. Please wait a minute and try again.';
  }
  const body = error.response?.data;
  if (error.response) {
    return (
      (typeof body === 'string' && body) ||
      body?.error ||
      'Could not generate right now. Please try again.'
    );
  }
  // Locally thrown errors (unparseable AI reply, empty result) carry their
  // message directly — there is no response body to read.
  return error.message || 'Could not generate right now. Please try again.';
};

interface BlockState {
  config: AiGeneratorBlockConfig;
  output: string;
  /** True once the visitor has hand-edited it — a "Generate All" after that no longer overwrites it. */
  edited: boolean;
  loading: boolean;
  error: string;
}

const initBlocks = (configs: AiGeneratorBlockConfig[]): BlockState[] =>
  configs.map((config) => ({ config, output: '', edited: false, loading: false, error: '' }));

interface AiGeneratorData {
  field_name?: string;
  placeholder?: string;
  buttonText?: string;
  mainInputLabel?: string;
  blocks?: AiGeneratorBlockConfig[];
  outputFormat?: AiGeneratorOutputFormat;
  /** Author-defined prompt chips: chip shows `label`, click fills `value`. */
  prompts?: (AiGeneratorPrompt | string)[];
  /** The author's default generation settings (type/tone/goal/persona/model/language). */
  aiConfig?: Partial<AiGeneratorAiConfig>;
  /** Which of those settings the respondent may change. */
  respondentEditable?: Partial<Record<AiConfigKey, boolean>>;
  /** Legacy single toggle — treated as "all editable" by resolveRespondentEditable. */
  respondentConfigurable?: boolean;
}

const AiGenerator = forwardRef<HTMLInputElement, any>((props, ref) => {
  const { disabled, read_only, onChange, mutable } = props;
  const data: AiGeneratorData = props.data || {};
  const { field_name, placeholder, buttonText, mainInputLabel, outputFormat = 'markdown' } = data;
  const blockConfigs = useMemo(() => data.blocks || [], [data.blocks]);

  // The AI call goes straight from the browser to the AI service in the
  // shared CRM tenant from the form URL (crmJwtToken authenticates it).
  const { getAnswers, crmJwtToken, pushAiConfig } = useContext(FormRuntimeContext);
  const aiTenantId = useCrmTenant();
  const tenantMissing = !aiTenantId || !crmJwtToken;

  // The builder canvas and the full-screen Form Preview render the same DOM
  // as the live form — a static, disabled preview — because neither carries a
  // FormRuntimeContext. Only the live page (`isLive`) is interactive.
  const isLive = mutable !== false && Boolean(getAnswers);

  // The author's defaults, then the visitor's session overrides on top — a
  // setting's override only applies when the author marked that setting
  // respondent-editable. Keyed on content, not object identity, so a
  // re-created field data object can't wipe an override mid-session.
  const authorConfigJson = JSON.stringify(resolveAiConfig(data.aiConfig));
  const authorConfig = useMemo<AiGeneratorAiConfig>(() => JSON.parse(authorConfigJson), [authorConfigJson]);
  const editableJson = JSON.stringify({
    respondentEditable: data.respondentEditable || null,
    respondentConfigurable: data.respondentConfigurable === true,
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const editable = useMemo(() => resolveRespondentEditable(JSON.parse(editableJson)), [editableJson]);
  const anyEditable = AI_CONFIG_KEYS.some((key) => editable[key]);

  const [overrides, setOverrides] = useState<AiGeneratorAiConfig>(authorConfig);
  useEffect(() => {
    setOverrides(authorConfig);
  }, [authorConfig]);

  // Personas are only loadable with a CRM token; fetched lazily (and cached
  // per tenant by fabaiApi) once the respondent can open the persona chip or
  // a stored persona value needs normalizing to the CRM id.
  const [personas, setPersonas] = useState<FabaiPersona[] | null>(null);

  /** What this request actually runs with: author value unless editable. */
  const aiConfig = useMemo(() => {
    const config = AI_CONFIG_KEYS.reduce(
      (acc, key) => {
        acc[key] = editable[key] ? overrides[key] : authorConfig[key];
        return acc;
      },
      {} as AiGeneratorAiConfig,
    );
    // The AI service and the workflow's `persona` variable want the CRM id,
    // not the display name — normalize once the persona list is known (older
    // forms stored the name).
    config.persona = resolvePersonaId(config.persona, personas);
    return config;
  }, [editable, overrides, authorConfig, personas]);

  const updateOverride = (patch: Partial<AiGeneratorAiConfig>) => {
    setOverrides((prev) => {
      const next = { ...prev, ...patch };
      // A goal that doesn't belong to the new type would be ignored by the AI
      // service's own grouping — swap it for the new list's first goal.
      if (patch.type && !getApplicableGoals(patch.type).includes(next.goal)) {
        next.goal = getApplicableGoals(patch.type)[0] || '';
      }
      return next;
    });
  };

  useEffect(() => {
    // The list is needed for the persona chip and to normalize a stored
    // value (older forms saved the display name) to the CRM id.
    if ((!editable.persona && !aiConfig.persona) || !isLive || personas !== null) return;
    if (!aiTenantId || !crmJwtToken) {
      setPersonas([]);
      return;
    }
    let cancelled = false;
    fetchPersonas(aiTenantId, crmJwtToken)
      .then((list) => {
        if (!cancelled) setPersonas(list);
      })
      .catch(() => {
        if (!cancelled) setPersonas([]);
      });
    return () => {
      cancelled = true;
    };
  }, [editable.persona, aiConfig.persona, isLive, personas, aiTenantId, crmJwtToken]);

  // The prefilled prompt chips the author defined, cleaned of blanks. Older
  // forms saved plain strings — normalize those to label/value pairs.
  const promptList = useMemo<AiGeneratorPrompt[]>(
    () =>
      (Array.isArray(data.prompts) ? data.prompts : [])
        .map((prompt): AiGeneratorPrompt => {
          if (typeof prompt === 'string') return { label: prompt, value: prompt };
          return {
            label: String(prompt?.label ?? '').trim(),
            value: String(prompt?.value ?? '').trim(),
          };
        })
        .filter((prompt) => prompt.value !== ''),
    [data.prompts],
  );

  const [mainInput, setMainInput] = useState('');
  const [blocks, setBlocks] = useState<BlockState[]>(() => initBlocks(blockConfigs));
  const [generatingAll, setGeneratingAll] = useState(false);
  const [formError, setFormError] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const anyLoading = generatingAll || blocks.some((block) => block.loading);
  const hiddenInputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => hiddenInputRef.current as HTMLInputElement);

  // The block list in the builder can change while editing; keep any already-
  // generated output for blocks that still exist, by id.
  useEffect(() => {
    setBlocks((prev) => {
      const byId = new Map(prev.map((block) => [block.config.id, block]));
      return blockConfigs.map((config) => byId.get(config.id) ?? { config, output: '', edited: false, loading: false, error: '' });
    });
  }, [blockConfigs]);

  const composed = useMemo(
    () => composeOutput(blocks.map((block) => ({ label: block.config.label, output: block.output })), outputFormat),
    [blocks, outputFormat],
  );

  // Publish this session's effective AI config (author defaults + chip edits)
  // under the reserved `_aiCfg:<field_name>` key, so a workflow variable bound
  // to this field resolves to what the respondent actually chose; the server
  // strips that key before saving anything. The form library never wires
  // `props.onChange` to custom elements (the composed value rides the hidden
  // input instead), so on the live form the reserved key goes out through the
  // renderer's context writer. That channel carries the config key only —
  // never the field value: a field-valued write changes ReactFormGenerator's
  // remount key and would wipe this field's generated blocks.
  useEffect(() => {
    if (!field_name) return;
    if (isLive && pushAiConfig) {
      pushAiConfig(field_name, { ...aiConfig });
    }
    if (onChange) {
      onChange(
        isLive
          ? {
              [field_name]: composed,
              [`${AI_OVERRIDE_PREFIX}${field_name}`]: { ...aiConfig },
            }
          : { [field_name]: composed },
      );
    }
    // `onChange` and `pushAiConfig` are fresh functions every render; only the
    // composed value, which field it belongs to and the effective config
    // (identity changes only when a value actually changes) should re-emit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [composed, field_name, isLive, aiConfig]);

  // The textarea's draft value is layered on top of `getAnswers()` (which
  // only reflects committed `onChange`s) so a block can reference
  // `{{thisField}}` as-you-type.
  const answersForRequest = () => ({
    ...(getAnswers?.() || {}),
    ...(field_name ? { [field_name]: mainInput } : {}),
  });

  // Nothing worth sending to the AI without a description.
  const hasDescription = mainInput.trim() !== '';

  const controlsDisabled = Boolean(disabled) || Boolean(read_only) || anyLoading;

  const generate = async (only?: string[]) => {
    if (!isLive || !field_name || tenantMissing || blocks.length === 0 || !hasDescription) return;

    if (only) {
      setBlocks((prev) => prev.map((block) => (only.includes(block.config.label) ? { ...block, loading: true, error: '' } : block)));
    } else {
      setGeneratingAll(true);
      setFormError('');
      setWarnings([]);
    }

    try {
      // The block prompts are this field's saved config, which the browser
      // already has to render the form — so the whole request is built here
      // and sent straight to the AI service (what the backend used to proxy).
      const answers = answersForRequest();
      const wanted = only ? blockConfigs.filter((block) => only.includes(block.label)) : blockConfigs;
      const blockInputs: AiBlockInput[] = wanted.map((block) => ({
        label: String(block.label || '').slice(0, MAX_LABEL),
        prompt: resolveBlockPrompt(String(block.prompt || ''), answers).slice(0, MAX_PROMPT),
      }));

      const response = await generateFabai({
        tenantId: aiTenantId as string,
        jwtToken: crmJwtToken,
        payload: {
          model: aiConfig.model,
          type: aiConfig.type,
          source: getSourceForType(aiConfig.type),
          onBehalfOf: 'user',
          tone: aiConfig.tone,
          goal: aiConfig.goal,
          persona: aiConfig.persona,
          language: aiConfig.language,
          context: {},
          userInput: buildBlocksUserInput(blockInputs),
        },
      });

      const text = extractAiText(response);
      if (!text) {
        throw new Error('The AI service returned an empty result. Please try again.');
      }
      const { outputs, warnings: nextWarnings } = parseBlockOutputs(
        text,
        blockInputs.map((block) => block.label),
      );
      const results = toBlockResults(blockInputs, outputs);

      const byLabel = new Map(results.map((result) => [result.label, result.output]));
      setBlocks((prev) =>
        prev.map((block) => {
          if (!byLabel.has(block.config.label)) return only ? { ...block, loading: false } : block;
          // A regenerate (solo or via Generate All) replaces even hand-edited
          // text — that is what "regenerate" means; editing again after is fine.
          return { ...block, output: byLabel.get(block.config.label) as string, edited: false, loading: false, error: '' };
        }),
      );
      if (!only) setWarnings(nextWarnings);
    } catch (err: any) {
      const message = errorMessage(err);
      if (only) {
        setBlocks((prev) => prev.map((block) => (only.includes(block.config.label) ? { ...block, loading: false, error: message } : block)));
      } else {
        setFormError(message);
      }
    } finally {
      if (!only) setGeneratingAll(false);
    }
  };

  const editBlock = (id: string, output: string) => {
    setBlocks((prev) => prev.map((block) => (block.config.id === id ? { ...block, output, edited: true } : block)));
  };

  const hasAnyOutput = blocks.some((block) => block.output.trim() !== '');

  // ── Settings chips: only the respondent-editable ones, in a CM-style strip ──
  const typeOption = AI_TYPES.find((option) => option.value === aiConfig.type);
  const goalList = getApplicableGoals(aiConfig.type);
  const shownGoals = goalList.includes(aiConfig.goal) ? goalList : [aiConfig.goal, ...goalList].filter(Boolean);
  const languageLabel = LANGUAGES.find((language) => language.value === aiConfig.language)?.label || aiConfig.language;
  const modelLabel = MODELS.find((model) => model.value === aiConfig.model)?.label || aiConfig.model;

  const typeOptions: ChipOption[] = AI_TYPES.map((option) => ({ value: option.value, label: option.label }));
  const goalOptions: ChipOption[] = shownGoals.map((goal) => ({ value: goal, label: goal }));
  const toneOptions: ChipOption[] = TONES.map((tone) => ({ value: tone, label: tone }));
  // Options carry the CRM id as the value — what the AI service and the
  // workflow variable want — and the persona's name purely for display.
  const personaOptions: ChipOption[] = [
    { value: '', label: 'None' },
    ...(personas || [])
      .filter((persona) => personaId(persona))
      .map((persona) => ({ value: personaId(persona), label: personaName(persona) })),
  ];
  const languageOptions: ChipOption[] = LANGUAGES.map((language) => ({ value: language.value, label: language.label }));
  const modelOptions: ChipOption[] = MODELS.map((model) => ({ value: model.value, label: model.label }));

  return (
    <StyledWrapper className={`ai-generator-field${isLive ? '' : ' ai-preview'}`}>
      {mainInputLabel && <label className="ai-main-label">{mainInputLabel}</label>}

      {/* The composer card — chip strip, prompt chips, textarea and toolbar,
          laid out like the CRM's own generator (RefinementPanel strip above
          the textarea, Generate at the bottom-right). On the builder canvas
          and in the Form Preview the same DOM renders, fully disabled. */}
      <div className="ai-composer">
        {anyEditable && (
          <div className="ai-chip-strip">
            {editable.type && (
              <SettingChip
                title="Type"
                icon={<EditNoteIcon />}
                iconColor="#8C62FF"
                display={typeOption?.label || ''}
                value={aiConfig.type}
                options={typeOptions}
                onChange={(value) => updateOverride({ type: value })}
                disabled={controlsDisabled || !isLive}
              />
            )}
            {editable.goal && (
              <SettingChip
                title="Goal"
                icon={<FlagIcon />}
                iconColor="#ef4444"
                display={aiConfig.goal}
                value={aiConfig.goal}
                options={goalOptions}
                onChange={(value) => updateOverride({ goal: value })}
                disabled={controlsDisabled || !isLive}
              />
            )}
            {editable.tone && (
              <SettingChip
                title="Tone"
                icon={<VolumeUpIcon />}
                iconColor="#00C8B3"
                display={aiConfig.tone}
                value={aiConfig.tone}
                options={toneOptions}
                onChange={(value) => updateOverride({ tone: value })}
                disabled={controlsDisabled || !isLive}
              />
            )}
            {editable.persona && (
              <SettingChip
                title="Persona"
                icon={<PeopleIcon />}
                iconColor="#FF8D28"
                display={personaDisplayName(aiConfig.persona, personas)}
                value={aiConfig.persona}
                options={personaOptions}
                onChange={(value) => updateOverride({ persona: value })}
                disabled={controlsDisabled || !isLive}
              />
            )}
          </div>
        )}

        {/* The author's prefilled prompts — the chip shows its label;
            clicking fills the textarea with its value (replacing whatever
            was typed). */}
        {promptList.length > 0 && (
          <div className="ai-prompt-chips">
            {promptList.map((prompt, index) => (
              <div
                key={`${index}-${prompt.value}`}
                className="ai-prompt-chip"
                title={prompt.value}
                onClick={() => setMainInput(prompt.value)}
              >
                {prompt.label || prompt.value}
              </div>
            ))}
          </div>
        )}

        <textarea
          className="ai-composer-textarea"
          name={field_name ? `${field_name}__input` : undefined}
          rows={3}
          placeholder={placeholder || 'Describe what you want to generate...'}
          value={mainInput}
          onChange={(e) => setMainInput(e.target.value)}
          disabled={controlsDisabled || !isLive}
        />

        <div className="ai-toolbar">
          <div className="ai-toolbar-left">
            {editable.language && (
              <SettingChip
                compact
                title="Language"
                icon={<LanguageIcon />}
                iconColor="#7c6fe0"
                display={languageLabel}
                value={aiConfig.language}
                options={languageOptions}
                onChange={(value) => updateOverride({ language: value })}
                disabled={controlsDisabled || !isLive}
              />
            )}
            {editable.model && (
              <SettingChip
                compact
                title="Model"
                icon={<SmartToyIcon />}
                iconColor="#4c34c2"
                display={modelLabel}
                value={aiConfig.model}
                options={modelOptions}
                onChange={(value) => updateOverride({ model: value })}
                disabled={controlsDisabled || !isLive}
              />
            )}
          </div>

          <button
            type="button"
            className="ai-generate-btn"
            onClick={() => generate()}
            disabled={!isLive || controlsDisabled || tenantMissing || blocks.length === 0 || !hasDescription}
          >
            {generatingAll ? (
              <>
                <CircularProgress size={14} sx={{ color: 'inherit' }} />
                Generating...
              </>
            ) : (
              <>
                <AutoAwesomeIcon sx={{ fontSize: 16 }} />
                {hasAnyOutput ? 'Regenerate All' : buttonText || 'Generate'}
              </>
            )}
          </button>
        </div>
      </div>

      {/* Authoring-time only: a quick reminder of what this field is set up to
          do — the composer above is a static preview, and the blocks
          themselves stay hidden until generated. */}
      {!isLive && (
        <div className="ai-summary">
          {blockConfigs.length || 0} content block{blockConfigs.length === 1 ? '' : 's'} configured.
          {' Visitors describe their input, then generate each block.'}
          {promptList.length > 0
            ? ` ${promptList.length} prefilled prompt chip${promptList.length === 1 ? '' : 's'}.`
            : ''}
          {anyEditable ? ' Visitors can adjust the AI settings before generating.' : ''}
        </div>
      )}

      {tenantMissing && <div className="ai-error-text">This field requires crmJwtToken in the form URL.</div>}
      {formError && <div className="ai-error-text">{formError}</div>}
      {warnings.map((warning) => (
        <div className="ai-warning-text" key={warning}>
          {warning}
        </div>
      ))}
      {blocks.length === 0 && <div className="ai-error-text">No content blocks are configured for this field.</div>}

      {/* A block that hasn't been generated (or attempted) yet stays fully
          hidden — nothing appears here until there's something to show. */}
      {blocks
        .filter((block) => block.output || block.loading || block.error)
        .map((block) => (
          <div className="ai-block-card" key={block.config.id}>
            <div className="ai-block-head">
              <div>
                <div className="ai-block-label">{block.config.label}</div>
                {block.config.description && <div className="ai-block-desc">{block.config.description}</div>}
              </div>
              {block.config.regenerable !== false && block.output && (
                <button
                  type="button"
                  className="ai-block-regenerate"
                  onClick={() => generate([block.config.label])}
                  disabled={!isLive || controlsDisabled || tenantMissing || !hasDescription}
                >
                  {block.loading ? <CircularProgress size={12} sx={{ color: 'inherit' }} /> : 'Regenerate'}
                </button>
              )}
            </div>

            {block.output && (
              <div className="ai-block-output-tag">AI generated{block.edited ? ' · edited' : ''}</div>
            )}

            {block.error && <div className="ai-error-text">{block.error}</div>}

            {(block.output || block.loading) &&
              (block.config.editable !== false ? (
                <textarea
                  className="ai-block-output"
                  rows={5}
                  value={block.output}
                  disabled={disabled || read_only || block.loading}
                  placeholder={block.loading ? 'Generating…' : ''}
                  onChange={(e) => editBlock(block.config.id, e.target.value)}
                />
              ) : (
                <div className="ai-block-output ai-block-output--readonly">
                  {block.loading ? 'Generating…' : block.output}
                </div>
              ))}
          </div>
        ))}

      <input ref={hiddenInputRef} type="hidden" name={field_name} value={composed} />
    </StyledWrapper>
  );
});

const StyledWrapper = styled.div`
  width: 100%;
  margin: 8px 0;
  font-family: inherit;
  box-sizing: border-box;

  .ai-main-label {
    display: block;
    font-size: 0.85rem;
    font-weight: 600;
    color: #475569;
    margin-bottom: 6px;
  }

  .ai-summary {
    font-size: 0.78rem;
    color: #7c3aed;
    background: #f5f3ff;
    border: 1px dashed #c4b5fd;
    border-radius: 6px;
    padding: 6px 10px;
    margin-top: 8px;
  }

  /* ── Composer card (customer-messenger MessageComposer style) ─────────── */
  .ai-composer {
    width: 100%;
    box-sizing: border-box;
    background: #ffffff;
    border: 1px solid #d0d5dd;
    border-radius: 12px;
    overflow: hidden;
  }

  /* Refinement strip: grey band, bottom hairline, chip row — like CM's
     .control-container / .select-row. */
  .ai-chip-strip {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    background: #f9fafb;
    border-bottom: 1px solid #d0d5dd;
    padding: 6px 12px;
  }

  /* Prefilled prompt chips — CM suggestion palette (#faf8ff / #ebe7ff). */
  .ai-prompt-chips {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    padding: 10px 10px;
  }

  .ai-prompt-chip {
    font-family: inherit;
    font-size: 12.5px;
    font-weight: 500;
    color: #2d235c;
    background: #faf8ff;
    border: 1px solid #ebe7ff;
    border-radius: 16px;
    padding: 5px 10px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover:not(:disabled) {
      background: #f3efff;
      border-color: #d6cdff;
    }

    &:active:not(:disabled) {
      transform: scale(0.98);
    }

    &:disabled {
      cursor: default;
      opacity: 0.75;
    }
  }

  .ai-composer-textarea {
    display: block;
    width: 100%;
    box-sizing: border-box;
    border: none;
    outline: none;
    resize: none;
    padding: 10px;
    min-height: 100px;
    max-height: 320px;
    font-family: inherit;
    font-size: 14px;
    line-height: 1.5;
    background: #ffffff;
    color: #1d2939;
    overflow-y: auto;

    &::placeholder {
      color: #98a2b3;
    }

    &:disabled {
      cursor: default;
    }
  }

  /* Bottom toolbar: language/model left, Generate right. */
  .ai-toolbar {
    width: 100%;
    box-sizing: border-box;
    display: flex;
    flex-wrap: wrap;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 5px;
    background: #ffffff;
  }

  .ai-toolbar-left {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-left: 5px;
    min-width: 0;
  }

  .ai-generate-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    gap: 6px;
    background: #4c34c2;
    color: #ffffff;
    border: 1px solid #4c34c2;
    border-radius: 8px;
    padding: 6px 14px;
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    cursor: pointer;
    transition: background 0.2s ease, border-color 0.2s ease;

    &:hover:not(:disabled) {
      background: #322187;
      border-color: #322187;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  /* Static preview (builder canvas / Form Preview): the composer renders
     exactly as on the live form, just inert — extra affordances off. */
  &.ai-preview {
    .ai-composer-textarea,
    .ai-prompt-chip {
      cursor: default;
    }
  }

  .ai-block-regenerate {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background: transparent;
    color: #5550ff;
    border: 1px solid #5550ff;
    border-radius: 4px;
    padding: 3px 10px;
    font-family: inherit;
    font-size: 0.75rem;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;

    &:hover:not(:disabled) {
      background: #5550ff;
      color: #ffffff;
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  .ai-error-text {
    color: #d32f2f;
    font-size: 0.75rem;
    margin-top: 6px;
  }

  .ai-warning-text {
    color: #b54708;
    font-size: 0.75rem;
    margin-top: 4px;
  }

  /* The whole block — label, description, output — sits in one visibly
     bordered card, distinct from an ordinary input, so it clearly reads as
     "this is what the AI produced" rather than part of the form's own fields. */
  .ai-block-card {
    margin-top: 12px;
    border: 1.5px solid #c7d2fe;
    background: #f5f3ff;
    border-radius: 8px;
    padding: 12px;
  }

  .ai-block-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 10px;
    margin-bottom: 6px;
  }

  .ai-block-label {
    font-size: 0.9rem;
    font-weight: 600;
    color: #1e293b;
  }

  .ai-block-desc {
    font-size: 0.78rem;
    color: #64748b;
    margin-top: 2px;
  }

  .ai-block-output-tag {
    display: inline-block;
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #5550ff;
    background: #ffffff;
    border: 1px solid #c7d2fe;
    border-radius: 4px;
    padding: 2px 6px;
    margin-bottom: 8px;
  }

  .ai-block-output {
    width: 100%;
    border: 1px solid #c4c4c4;
    border-radius: 4px;
    padding: 8.5px 14px;
    font-size: 1rem;
    font-family: inherit;
    outline: none;
    box-sizing: border-box;
    resize: vertical;
    overflow: auto;
    min-height: 90px;
    max-height: 640px;
    line-height: 1.6;
    background: #ffffff;
    color: #1e293b;
    transition: border-color 0.15s ease;

    &:focus {
      border-color: #0069ff;
    }

    &:disabled {
      background: #f8fafc;
      opacity: 0.7;
    }
  }

  .ai-block-output--readonly {
    min-height: 40px;
    white-space: pre-wrap;
    cursor: default;
  }
`;

AiGenerator.displayName = 'AiGenerator';
export default AiGenerator;
